/**
 * Whiteboard Autosave
 * static/js/whiteboard/autosave.js
 * 
 * 10 秒自動儲存功能
 */

import { AUTOSAVE_CONFIG, MESSAGES } from './config.js';
import * as state from './state.js';
import * as api from './api.js';
import { updateAutosaveIndicator } from './ui.js';
import { getSceneJson } from './export.js';

let autosaveTimer = null;
let debounceTimer = null;
let isAutosaving = false;
let autosaveEnabled = false;

// 協作廣播節流
let lastBroadcastTime = 0;
const BROADCAST_THROTTLE_MS = 100; // 100ms 節流

/**
 * 開始自動儲存
 * @param {number} intervalSeconds - 儲存間隔（秒）
 */
export function startAutosave(intervalSeconds = AUTOSAVE_CONFIG.intervalSeconds) {
  stopAutosave();

  const intervalMs = intervalSeconds * 1000;

  autosaveTimer = setInterval(async () => {
    await performAutosave();
  }, intervalMs);

  console.log(`Autosave started with ${intervalSeconds}s interval`);
}

/**
 * 停止自動儲存
 */
export function stopAutosave() {
  if (autosaveTimer) {
    clearInterval(autosaveTimer);
    autosaveTimer = null;
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  console.log('Autosave stopped');
}

/**
 * 執行自動儲存
 */
async function performAutosave() {
  // 防止重複儲存
  if (isAutosaving) {
    return;
  }

  const drawingId = state.getCurrentDrawingId();
  if (!drawingId) {
    // 沒有當前白板 ID，無法自動儲存
    return;
  }

  // 檢查是否有變更
  if (!state.hasChanges()) {
    return;
  }

  try {
    isAutosaving = true;
    updateAutosaveIndicator('saving');

    const sceneJson = getSceneJson();
    if (!sceneJson) {
      throw new Error('Failed to get scene JSON');
    }

    await api.saveDraft(drawingId, sceneJson);

    // 更新 hash
    const excalidrawAPI = state.getExcalidrawAPI();
    if (excalidrawAPI) {
      const elements = excalidrawAPI.getSceneElements();
      state.setLastSavedHash(state.hashElements(elements));
    }

    state.setDirty(false);
    updateAutosaveIndicator('saved');
  } catch (error) {
    console.error('Autosave failed:', error);
    updateAutosaveIndicator('error');
  } finally {
    isAutosaving = false;
  }
}

/**
 * 觸發防抖儲存（當用戶停止操作後儲存）
 * @param {number} delayMs - 延遲毫秒數
 */
export function triggerDebouncedSave(delayMs = AUTOSAVE_CONFIG.debounceMs) {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  state.setDirty(true);
  updateAutosaveIndicator('dirty');

  debounceTimer = setTimeout(async () => {
    await performAutosave();
  }, delayMs);
}

/**
 * 立即儲存（用於用戶主動儲存）
 * @returns {Promise<boolean>}
 */
export async function saveNow() {
  const drawingId = state.getCurrentDrawingId();
  if (!drawingId) {
    return false;
  }

  try {
    isAutosaving = true;
    updateAutosaveIndicator('saving');

    const sceneJson = getSceneJson();
    if (!sceneJson) {
      throw new Error('Failed to get scene JSON');
    }

    await api.saveDraft(drawingId, sceneJson);

    // 更新 hash
    const excalidrawAPI = state.getExcalidrawAPI();
    if (excalidrawAPI) {
      const elements = excalidrawAPI.getSceneElements();
      state.setLastSavedHash(state.hashElements(elements));
    }

    state.setDirty(false);
    updateAutosaveIndicator('saved');

    return true;
  } catch (error) {
    console.error('Save failed:', error);
    updateAutosaveIndicator('error');
    return false;
  } finally {
    isAutosaving = false;
  }
}

/**
 * 檢查自動儲存是否正在運行
 * @returns {boolean}
 */
export function isRunning() {
  return autosaveTimer !== null;
}

/**
 * 檢查自動儲存是否啟用
 * @returns {boolean}
 */
export function isAutosaveEnabled() {
  return autosaveEnabled;
}

/**
 * 設定自動儲存啟用狀態
 * @param {boolean} enabled - 是否啟用
 * @param {number} intervalSeconds - 儲存間隔（秒）
 */
export function setAutosaveEnabled(enabled, intervalSeconds) {
  autosaveEnabled = enabled;
  if (enabled) {
    startAutosave(intervalSeconds);
  } else {
    stopAutosave();
    updateAutosaveIndicator('disabled');
  }
  console.log(`Autosave ${enabled ? 'enabled' : 'disabled'}`);
}

/**
 * 檢查是否正在儲存中
 * @returns {boolean}
 */
export function isSaving() {
  return isAutosaving;
}

/**
 * 場景變更處理器（供 Excalidraw onChange 使用）
 * @param {Array} elements 
 * @param {Object} appState 
 */
export function onSceneChange(elements, appState) {
  // 標記有未儲存變更
  state.setDirty(true);
  updateAutosaveIndicator('dirty');
  
  // 協作廣播（節流）
  const now = Date.now();
  if (now - lastBroadcastTime >= BROADCAST_THROTTLE_MS) {
    lastBroadcastTime = now;
    // 動態導入協作模組避免循環依賴
    import('./collaboration.js').then(collaboration => {
      if (collaboration.isCollaborating()) {
        // 使用 API 獲取最新元素，而不是使用 onChange 傳入的元素
        // onChange 傳入的 elements 可能是內部引用，會被修改
        const api = state.getExcalidrawAPI();
        if (api) {
          const currentElements = api.getSceneElements();
          collaboration.broadcastSceneUpdate(currentElements);
        }
      }
    });
  }
}
